import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    ArrowLeft,
    Download,
    Copy,
    RefreshCw,
    AlertTriangle,
    Save,
    FileText,
    Shield,
    Compass,
    Skull,
    HeartPulse,
    Anchor,
    Check,
    Mail,
    Sparkles,
    Edit3,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import CrisisBanner from "@/components/common/CrisisBanner";
import ChipGroup from "@/components/common/ChipGroup";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/tracker";
import {
    STEPS,
    EMPTY_ANSWERS,
    COUNTRIES,
    ESTADO_OPTIONS,
    URGENCIA_OPTIONS,
    SIGNAL_OPTIONS,
    EMOTION_OPTIONS,
    LIE_OPTIONS,
    PLACE_OPTIONS,
    PEOPLE_OPTIONS,
    HOURS_OPTIONS,
    CONSEQ_BODY,
    CONSEQ_BONDS,
    CONSEQ_MONEY,
    CONSEQ_DIGNITY,
    EXIT_ACTION_OPTIONS,
    ANCHOR_PHRASES,
    BASE_HABITS,
    STRUCTURAL_LIMITS,
    scanCrisis,
    buildMaps,
    chipValues,
    getOpposite,
} from "@/lib/mapaInversoSchema";

const STORAGE_KEY = "ad_sa_mapa_inverso_v2";

function loadDraft() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function persistDraft(state) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        /* ignore */
    }
}

function clearDraft() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

// ---------- Atoms ----------
function Label({ children, hint }) {
    return (
        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
            {children}
            {hint && (
                <span className="ml-2 normal-case tracking-normal text-[var(--ink-muted)] text-[11px]">
                    {hint}
                </span>
            )}
        </label>
    );
}

function TextInput(props) {
    return <input {...props} className="input-dossier" />;
}

function TextArea(props) {
    return (
        <textarea
            {...props}
            className="input-dossier resize-y leading-relaxed"
            rows={props.rows || 3}
        />
    );
}

function Annotation({ children }) {
    return (
        <p className="font-hand text-2xl text-[var(--warning)] -rotate-1 inline-block mt-6">
            {children}
        </p>
    );
}

function StepCard({ code, title, subtitle, children, testid }) {
    return (
        <div className="dossier" data-testid={testid || `step-card-${code}`}>
            <div className="dossier-header">
                <span>{code} · {subtitle}</span>
                <span className="text-[var(--danger)]">Confidencial</span>
            </div>
            <div className="p-6 md:p-10">
                <p className="eyebrow mb-3">Paso {code}</p>
                <h2 className="font-display text-3xl md:text-4xl mb-8 leading-[0.95]">
                    {title}
                </h2>
                {children}
            </div>
        </div>
    );
}

// ---------- Step renderers ----------
function StepWelcome({ onStart, hasDraft, onResume }) {
    return (
        <div className="dossier" data-testid="step-welcome">
            <div className="dossier-header">
                <span>00 · Apertura · Confidencial</span>
                <span className="text-[var(--danger)]">Lectura responsable</span>
            </div>
            <div className="p-8 md:p-12">
                <div className="grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-8">
                        <p className="eyebrow mb-4">Mapa Inverso · expediente interactivo</p>
                        <h1 className="font-display text-4xl md:text-6xl leading-[0.9] mb-6">
                            Tu mapa no está aquí para condenarte.{" "}
                            <span className="text-[var(--danger)]">Está aquí para mostrarte por dónde no volver.</span>
                        </h1>
                        <p className="font-mono text-base text-[var(--paper)] leading-relaxed mb-4">
                            Esta es una herramienta de autoconciencia, no un
                            tratamiento ni una terapia. Vas a marcar opciones,
                            elegir señales y construir, paso a paso, dos rutas:
                        </p>
                        <ul className="font-mono text-sm text-[var(--paper)] leading-relaxed list-none pl-0 mb-8 space-y-2">
                            <li className="pl-6 relative">
                                <span className="absolute left-0 text-[var(--danger)]">✕</span>
                                <strong className="text-[var(--paper-2)]">Mi Mapa al Infierno:</strong>{" "}
                                la ruta vieja. La que ya tienes memorizada.
                            </li>
                            <li className="pl-6 relative">
                                <span className="absolute left-0 text-[var(--warning)]">✓</span>
                                <strong className="text-[var(--paper-2)]">Mi Mapa de Salida:</strong>{" "}
                                acciones contrarias generadas para cada señal.
                            </li>
                        </ul>
                        <div className="border border-dashed border-[var(--warning)] p-5 mb-8">
                            <p className="font-mono text-xs text-[var(--warning)] uppercase tracking-[0.25em] mb-2">
                                <AlertTriangle className="inline w-3.5 h-3.5 mr-2 -mt-0.5" />
                                Aviso responsable
                            </p>
                            <p className="font-mono text-sm text-[var(--paper)] leading-relaxed">
                                Si en algún momento aparece la idea de hacerte
                                daño o ideación suicida, detén el ejercicio y
                                llama a una línea de ayuda. El sitio te mostrará
                                un aviso automático cuando detecte señales.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={onStart}
                                className="btn-brutal btn-brutal-danger"
                                data-testid="mapa-start-btn"
                            >
                                Comenzar el mapa
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            {hasDraft && (
                                <button
                                    onClick={onResume}
                                    className="btn-brutal btn-brutal-ghost"
                                    data-testid="mapa-resume-btn"
                                >
                                    <Save className="w-4 h-4" />
                                    Retomar sesión guardada
                                </button>
                            )}
                            <Link
                                to="/salida"
                                className="btn-brutal"
                                data-testid="mapa-emergency-btn"
                            >
                                <Shield className="w-4 h-4" />
                                Salida de emergencia
                            </Link>
                        </div>
                    </div>
                    <aside className="lg:col-span-4 border-l-2 border-dashed border-[var(--ink-dim)] pl-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-3">
                            Qué incluye
                        </p>
                        {STEPS.slice(1, -1).map((s) => (
                            <div
                                key={s.id}
                                className="flex items-baseline gap-3 py-2 border-b border-dashed border-[var(--ink-dim)] last:border-0"
                            >
                                <span className="font-mono text-xs text-[var(--ink-dim)]">
                                    {s.code}
                                </span>
                                <span className="font-mono text-sm text-[var(--paper-2)]">
                                    {s.title}
                                </span>
                            </div>
                        ))}
                        <p className="font-hand text-xl text-[var(--warning)] -rotate-2 mt-4 inline-block">
                            Este no es un test. Es un espejo.
                        </p>
                    </aside>
                </div>
            </div>
        </div>
    );
}

function StepStarting({ data, setData }) {
    const v = data.starting || {};
    const upd = (k, val) =>
        setData({ ...data, starting: { ...v, [k]: val } });
    const showAlert = v.urgencia === "Estoy en riesgo ahora";
    return (
        <StepCard
            code="01"
            title="Punto de partida"
            subtitle="Diagnóstico inicial"
        >
            <div className="space-y-7">
                <div>
                    <Label hint="(sustancia, compulsión, conducta evasiva)">
                        ¿Qué consumes o repites?
                    </Label>
                    <TextInput
                        type="text"
                        value={v.sustancia || ""}
                        onChange={(e) => upd("sustancia", e.target.value)}
                        placeholder="Ej: cocaína, alcohol, pornografía, trabajo compulsivo…"
                        data-testid="step1-sustancia"
                        autoFocus
                    />
                </div>

                <div>
                    <Label>Estado actual</Label>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {ESTADO_OPTIONS.map((opt) => {
                            const active = v.estado === opt;
                            return (
                                <button
                                    key={opt}
                                    type="button"
                                    onClick={() => upd("estado", opt)}
                                    className={`text-left font-mono text-xs px-3 py-3 border transition-all ${
                                        active
                                            ? "bg-[var(--paper-2)] text-black border-[var(--paper-2)] shadow-[3px_3px_0_var(--danger)]"
                                            : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--danger)]"
                                    }`}
                                    data-testid={`step1-estado-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                                    aria-pressed={active}
                                >
                                    {active && (
                                        <Check className="inline w-3 h-3 mr-2 -mt-0.5" />
                                    )}
                                    {opt}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <Label>Nivel de urgencia</Label>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        {URGENCIA_OPTIONS.map((opt) => {
                            const active = v.urgencia === opt.value;
                            const danger = opt.critical;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => upd("urgencia", opt.value)}
                                    className={`font-mono text-xs px-3 py-3 border transition-all ${
                                        active
                                            ? danger
                                                ? "bg-[var(--danger)] text-[var(--paper-2)] border-[var(--danger)] shadow-[3px_3px_0_#000]"
                                                : "bg-[var(--paper-2)] text-black border-[var(--paper-2)] shadow-[3px_3px_0_var(--danger)]"
                                            : danger
                                              ? "border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-[var(--paper-2)]"
                                              : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--warning)]"
                                    }`}
                                    data-testid={`step1-urgencia-${opt.value.toLowerCase().replace(/\s+/g, "-")}`}
                                    aria-pressed={active}
                                >
                                    {opt.value}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {showAlert && (
                    <div
                        className="border-2 border-[var(--danger)] bg-[#1a0606] p-5"
                        data-testid="urgent-risk-alert"
                    >
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--danger)] mb-3">
                            <AlertTriangle className="inline w-4 h-4 mr-2 -mt-0.5" />
                            Riesgo inmediato detectado
                        </p>
                        <p className="font-mono text-sm text-[var(--paper-2)] leading-relaxed mb-3">
                            Esta herramienta <strong>no reemplaza ayuda profesional</strong>.
                            Si estás en riesgo de hacerte daño o hacerle daño a
                            alguien, busca ayuda inmediata o llama a emergencias
                            de tu país.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <a
                                href="tel:6003607777"
                                className="font-mono text-xs uppercase tracking-[0.2em] bg-[var(--danger)] text-[var(--paper-2)] px-4 py-2 hover:bg-[var(--paper-2)] hover:text-black"
                                data-testid="urgent-call-1"
                            >
                                Chile · 600 360 7777
                            </a>
                            <a
                                href="tel:4141"
                                className="font-mono text-xs uppercase tracking-[0.2em] border border-[var(--warning)] text-[var(--warning)] px-4 py-2 hover:bg-[var(--warning)] hover:text-black"
                                data-testid="urgent-call-2"
                            >
                                Línea suicidio · *4141
                            </a>
                            <Link
                                to="/salida"
                                className="font-mono text-xs uppercase tracking-[0.2em] border border-[var(--paper-2)] text-[var(--paper-2)] px-4 py-2 hover:bg-[var(--paper-2)] hover:text-black"
                            >
                                Salida de emergencia
                            </Link>
                        </div>
                    </div>
                )}
            </div>
            <Annotation>Empezamos por el dato más simple. La verdad cabe en una línea.</Annotation>
        </StepCard>
    );
}

function ChipStep({ data, setData, code, title, subtitle, field, question, options, max, intent, annotation }) {
    const v = data[field];
    return (
        <StepCard code={code} title={title} subtitle={subtitle}>
            <p className="font-mono text-sm text-[var(--paper)] mb-6 leading-relaxed">
                {question}
            </p>
            <ChipGroup
                options={options}
                value={v}
                onChange={(next) => setData({ ...data, [field]: next })}
                max={max}
                testId={field}
                intent={intent}
            />
            {annotation && <Annotation>{annotation}</Annotation>}
        </StepCard>
    );
}

function StepTriggers({ data, setData }) {
    const v = data.triggers || {};
    const upd = (k, val) =>
        setData({ ...data, triggers: { ...v, [k]: val } });
    return (
        <StepCard
            code="05"
            title="Lugares, personas y horarios"
            subtitle="Cartografía del riesgo"
        >
            <div className="space-y-10">
                <ChipGroup
                    label="Lugares de riesgo"
                    options={PLACE_OPTIONS}
                    value={v.places}
                    onChange={(next) => upd("places", next)}
                    testId="places"
                />
                <ChipGroup
                    label="Personas asociadas a la ruta"
                    options={PEOPLE_OPTIONS}
                    value={v.people}
                    onChange={(next) => upd("people", next)}
                    testId="people"
                />
                <ChipGroup
                    label="Horarios críticos"
                    options={HOURS_OPTIONS}
                    value={v.hours}
                    onChange={(next) => upd("hours", next)}
                    testId="hours"
                />
            </div>
            <Annotation>Dibujar la ruta no la legitima. La desactiva.</Annotation>
        </StepCard>
    );
}

function StepConsequences({ data, setData }) {
    const v = data.consequences || {};
    const upd = (k, val) =>
        setData({ ...data, consequences: { ...v, [k]: val } });
    return (
        <StepCard
            code="06"
            title="Consecuencias"
            subtitle="Lo que ya pagaste por la ruta"
        >
            <div className="space-y-10">
                <ChipGroup
                    label="Cuerpo / salud"
                    options={CONSEQ_BODY}
                    value={v.body}
                    onChange={(next) => upd("body", next)}
                    testId="body"
                />
                <ChipGroup
                    label="Vínculos"
                    options={CONSEQ_BONDS}
                    value={v.bonds}
                    onChange={(next) => upd("bonds", next)}
                    testId="bonds"
                />
                <ChipGroup
                    label="Dinero / vida"
                    options={CONSEQ_MONEY}
                    value={v.money}
                    onChange={(next) => upd("money", next)}
                    testId="money"
                />
                <ChipGroup
                    label="Dignidad / autoestima"
                    options={CONSEQ_DIGNITY}
                    value={v.dignity}
                    onChange={(next) => upd("dignity", next)}
                    testId="dignity"
                    intent="danger"
                />
            </div>
            <Annotation>No hay puntaje. La factura ya estaba cobrada antes de escribirla.</Annotation>
        </StepCard>
    );
}

function StepImmediateExit({ data, setData }) {
    const v = data.immediate_exit || {};
    const upd = (k, val) =>
        setData({ ...data, immediate_exit: { ...v, [k]: val } });

    // Suggestion: derive from signals / emotions
    const suggestedAction = useMemo(() => {
        const sigs = chipValues(data.signals);
        const emos = chipValues(data.emotions);
        for (const s of [...sigs, ...emos]) {
            const opp = getOpposite(s);
            if (opp) return opp;
        }
        return null;
    }, [data.signals, data.emotions]);

    return (
        <StepCard
            code="07"
            title="Salida inmediata"
            subtitle="Ruta contraria · accionable hoy"
        >
            <div className="space-y-8">
                <div>
                    <Label hint="(nombre o iniciales — alguien que sí sabe escuchar)">
                        Persona segura
                    </Label>
                    <TextInput
                        type="text"
                        value={v.persona_segura || ""}
                        onChange={(e) => upd("persona_segura", e.target.value)}
                        placeholder="Ej: María (hermana), Carlos (amigo de NA)…"
                        data-testid="step7-persona"
                    />
                </div>

                <ChipGroup
                    label="Acciones contrarias inmediatas"
                    hint="Selecciona las que más te sirven"
                    options={EXIT_ACTION_OPTIONS}
                    value={v.actions}
                    onChange={(next) => upd("actions", next)}
                    testId="exit-actions"
                />

                {suggestedAction && (!v.actions?.selected?.length) && (
                    <div className="border border-dashed border-[var(--warning)] p-4 bg-[#0d0d0d]">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--warning)] mb-1 flex items-center gap-2">
                            <Sparkles className="w-3 h-3" />
                            Sugerencia basada en tus señales
                        </p>
                        <p className="font-mono text-sm text-[var(--paper-2)]">
                            “{suggestedAction}”
                        </p>
                    </div>
                )}

                <div>
                    <Label>Límite urgente · qué dejas de hacer hoy</Label>
                    <TextArea
                        value={v.limite || ""}
                        onChange={(e) => upd("limite", e.target.value)}
                        placeholder="Ej: no entrar al grupo de WhatsApp, no pasar por X esquina…"
                        data-testid="step7-limite"
                    />
                </div>

                <ChipGroup
                    label="Frase ancla · la que te repites cuando aprieta"
                    hint="Puedes elegir varias o escribir la tuya en Otro"
                    options={ANCHOR_PHRASES}
                    value={v.anchor}
                    onChange={(next) => upd("anchor", next)}
                    testId="anchor"
                />
            </div>
            <Annotation>Si tu ruta vieja empieza con silencio, tu ruta nueva empieza hablando.</Annotation>
        </StepCard>
    );
}

function StepRecovery({ data, setData }) {
    const v = data.recovery_system || {};
    const a = data.attempts || {};
    const upd = (k, val) =>
        setData({ ...data, recovery_system: { ...v, [k]: val } });
    const updA = (k, val) =>
        setData({ ...data, attempts: { ...a, [k]: val } });
    return (
        <StepCard
            code="08"
            title="Sistema de recuperación"
            subtitle="Base que sostiene · más allá del momento"
        >
            <div className="space-y-10">
                <ChipGroup
                    label="Hábito base"
                    hint="Selecciona el o los hábitos que vas a sostener"
                    options={BASE_HABITS}
                    value={v.habits}
                    onChange={(next) => upd("habits", next)}
                    testId="habits"
                />
                <ChipGroup
                    label="Límite estructural"
                    hint="Qué entorno necesitas modificar"
                    options={STRUCTURAL_LIMITS}
                    value={v.limits}
                    onChange={(next) => upd("limits", next)}
                    testId="limits"
                />
                <div>
                    <Label hint="(breve · para qué quieres caminar en dirección contraria)">
                        Propósito
                    </Label>
                    <TextArea
                        value={v.proposito || ""}
                        onChange={(e) => upd("proposito", e.target.value)}
                        placeholder="Hijos, salud, dignidad, escribir un libro, volver a confiar…"
                        data-testid="step8-proposito"
                    />
                </div>
                <div>
                    <Label hint="(0 a 50 — una cifra honesta)">
                        ¿Cuántas veces lo intentaste antes?
                    </Label>
                    <TextInput
                        type="number"
                        min="0"
                        max="50"
                        value={a.intentos_count ?? ""}
                        onChange={(e) => updA("intentos_count", e.target.value)}
                        placeholder="Ej: 3"
                        data-testid="step8-intentos"
                    />
                </div>
            </div>
            <Annotation>Lo que funcionó dos días puede funcionar de nuevo.</Annotation>
        </StepCard>
    );
}

// ---------- RESULT (editable comparison + identity capture + actions) ----------
function ResultView({ identity, setIdentity, answers, sessionId, onReset, pdfRef, aiGuide, setAiGuide }) {
    const maps = useMemo(() => buildMaps(answers), [answers]);
    const [comparison, setComparison] = useState(maps.comparison);
    const [emailLoading, setEmailLoading] = useState(false);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    useEffect(() => {
        // Reset comparison when answers change (after reset)
        setComparison(maps.comparison);
    }, [maps.comparison]);

    const code = useMemo(
        () => `MI-${(sessionId || crypto.randomUUID()).slice(0, 8).toUpperCase()}`,
        [sessionId],
    );
    const today = useMemo(
        () =>
            new Date().toLocaleDateString("es-CL", {
                year: "numeric",
                month: "short",
                day: "2-digit",
            }),
        [],
    );

    function editRow(idx, field, value) {
        setComparison((c) =>
            c.map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
        );
    }
    function removeRow(idx) {
        setComparison((c) => c.filter((_, i) => i !== idx));
    }

    async function fetchAIGuide() {
        setAiLoading(true);
        try {
            const { data } = await api.post("/mapa-inverso/ai-guide", {
                answers,
                summary: maps,
                nickname: identity.name || null,
                crisis_flagged: scanCrisis(answers),
            });
            setAiGuide(data.guide || "");
            toast.success("Guía personalizada lista.");
            return data.guide || "";
        } catch (e) {
            toast.error("No se pudo generar la guía con IA.");
            return "";
        } finally {
            setAiLoading(false);
        }
    }

    async function downloadPdf() {
        if (!pdfRef.current) return;
        setPdfBusy(true);
        try {
            // Ensure AI guide is present before printing
            let currentGuide = aiGuide;
            if (!currentGuide) {
                currentGuide = await fetchAIGuide();
            }
            // Wait a frame so the DOM updates
            await new Promise((r) => requestAnimationFrame(r));
            const mod = await import("html2pdf.js");
            const html2pdf = mod.default || mod;
            await html2pdf()
                .from(pdfRef.current)
                .set({
                    margin: 0,
                    filename: `mapa-inverso-${(identity.name || "expediente").replace(/\s+/g, "-").toLowerCase()}.pdf`,
                    image: { type: "jpeg", quality: 0.95 },
                    html2canvas: {
                        scale: 2,
                        backgroundColor: "#111111",
                        useCORS: true,
                        windowWidth: 794, // 210mm @ 96dpi
                    },
                    jsPDF: {
                        unit: "mm",
                        format: "a4",
                        orientation: "portrait",
                    },
                    pagebreak: { mode: ["css", "legacy"] },
                })
                .save();
            toast.success("PDF generado.");
        } catch (e) {
            toast.error("No se pudo generar el PDF. Intenta de nuevo.");
        } finally {
            setPdfBusy(false);
        }
    }

    function buildPlainText() {
        const lines = [
            `MAPA INVERSO — ${code}`,
            `Fecha: ${today}`,
            identity.name ? `Nombre: ${identity.name}` : null,
            identity.country ? `País: ${identity.country}` : null,
            "",
            "================================================",
            "MI MAPA AL INFIERNO",
            "================================================",
            `Sustancia/conducta: ${maps.infierno.sustancia}`,
            `Estado: ${maps.infierno.estado}`,
            `Urgencia: ${maps.infierno.urgencia}`,
            "",
            "TABLA COMPARATIVA · Ruta vieja → Ruta nueva",
            ...comparison.map(
                (r) => `   [${r.category}] ${r.old}  →  ${r.new}`,
            ),
            "",
            "================================================",
            "MI MAPA DE SALIDA",
            "================================================",
            `Persona segura: ${maps.salida.persona_segura}`,
            `Límite urgente: ${maps.salida.limite}`,
            `Propósito: ${maps.salida.proposito}`,
            "Acciones inmediatas: " + (maps.salida.acciones.join(" · ") || "—"),
            "Frases ancla: " + (maps.salida.frases_ancla.join(" · ") || "—"),
            "Hábitos base: " + (maps.salida.habitos.join(" · ") || "—"),
            "Límites estructurales: " + (maps.salida.limites_estructurales.join(" · ") || "—"),
            "",
            "================================================",
            "PLAN DE 24 HORAS",
            "================================================",
            ...maps.plan24h.map((p, i) => `${String(i + 1).padStart(2, "0")}. ${p}`),
            "",
            "================================================",
            "SI NECESITAS AYUDA HOY",
            "================================================",
            "SinAdicciones.org — orientación gratuita,",
            "guía sobre tratamientos y acompañamiento",
            "para personas y familias en adicción.",
            "",
            "— Nelson González Rodríguez · @nitanadicto",
            "elmapainverso.com · sinadicciones.org · plansobrio.com",
        ].filter(Boolean);
        return lines.join("\n");
    }

    async function copyText() {
        try {
            await navigator.clipboard.writeText(buildPlainText());
            toast.success("Plan copiado al portapapeles.");
        } catch {
            toast.error("Tu navegador bloqueó el portapapeles.");
        }
    }

    async function emailMe() {
        if (!identity.email) {
            toast.error("Ingresa un correo en el panel de la izquierda.");
            return;
        }
        setEmailLoading(true);
        try {
            await api.post("/mapa-inverso/email", {
                email: identity.email,
                answers,
                summary: maps,
                nickname: identity.name,
            });
            toast.success("Te enviamos el mapa por correo.");
        } catch (e) {
            toast.error(
                formatApiError(e.response?.data?.detail) ||
                    "No se pudo enviar el correo.",
            );
        } finally {
            setEmailLoading(false);
        }
    }

    return (
        <div className="space-y-8" data-testid="step-result">
            {/* Header card */}
            <div className="dossier">
                <div className="dossier-header">
                    <span>Exp. {code} · Mapa Inverso</span>
                    <span className="text-[var(--danger)]">Resultado</span>
                </div>
                <div className="p-6 md:p-10">
                    <p className="eyebrow mb-3">Expediente final</p>
                    <h2 className="font-display text-4xl md:text-5xl mb-3">
                        Acá está tu{" "}
                        <span className="text-[var(--danger)]">Mapa Inverso</span>
                        {identity.name ? `, ${identity.name}` : ""}.
                    </h2>
                    <p className="font-mono text-sm text-[var(--paper)] leading-relaxed max-w-2xl">
                        A la izquierda, la ruta que ya conoces. A la derecha,
                        las acciones contrarias generadas a partir de tus
                        respuestas. Puedes editar o eliminar filas antes de
                        descargar.
                    </p>
                </div>
            </div>

            {/* Optional identity capture (voluntary) */}
            <div className="border border-dashed border-[var(--ink-dim)] bg-[#0d0d0d] p-6 md:p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-3 flex items-center gap-2">
                    <Mail className="w-3 h-3" />
                    Voluntario · sólo si quieres recibir el mapa por correo
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                        <Label>Nombre o alias</Label>
                        <TextInput
                            type="text"
                            value={identity.name}
                            onChange={(e) =>
                                setIdentity({ ...identity, name: e.target.value })
                            }
                            placeholder="Cómo te llamamos"
                            data-testid="identity-name"
                        />
                    </div>
                    <div>
                        <Label>Email (opcional)</Label>
                        <TextInput
                            type="email"
                            value={identity.email}
                            onChange={(e) =>
                                setIdentity({ ...identity, email: e.target.value })
                            }
                            placeholder="tu@correo.cl"
                            data-testid="identity-email"
                        />
                    </div>
                    <div>
                        <Label>País (opcional)</Label>
                        <select
                            value={identity.country}
                            onChange={(e) =>
                                setIdentity({
                                    ...identity,
                                    country: e.target.value,
                                })
                            }
                            className="input-dossier"
                            data-testid="identity-country"
                        >
                            <option value="" style={{ background: "#111" }}>
                                — Seleccionar —
                            </option>
                            {COUNTRIES.map((c) => (
                                <option key={c} value={c} style={{ background: "#111" }}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <p className="font-mono text-[10px] text-[var(--ink-muted)] mt-3 leading-relaxed">
                    Si dejas tu email, guardaremos esta sesión para análisis
                    agregados sobre adicción en Latinoamérica. Sin email, todo
                    queda 100 % local en tu navegador.
                </p>
            </div>

            {/* Maps summary boxes */}
            <div className="grid lg:grid-cols-2 gap-6">
                <article className="border-2 border-[var(--danger)] bg-[#0d0d0d]">
                    <div className="bg-[var(--danger)] text-[var(--paper-2)] px-5 py-2 font-mono text-[11px] uppercase tracking-[0.25em] flex justify-between items-center">
                        <span>
                            <Skull className="inline w-3.5 h-3.5 mr-2 -mt-0.5" />
                            Mi Mapa al Infierno
                        </span>
                        <span>Ruta vieja</span>
                    </div>
                    <div className="p-6 space-y-3 font-mono text-sm">
                        <Row k="Sustancia" v={maps.infierno.sustancia} />
                        <Row k="Estado" v={maps.infierno.estado} />
                        <Row k="Urgencia" v={maps.infierno.urgencia} danger={maps.infierno.urgencia === "Estoy en riesgo ahora"} />
                        <ListRow k="Señales" arr={maps.infierno.signals} />
                        <ListRow k="Emociones" arr={maps.infierno.emotions} />
                        <ListRow k="Mentiras" arr={maps.infierno.lies} italic />
                        <ListRow k="Lugares · personas · horarios" arr={[...maps.infierno.places, ...maps.infierno.people, ...maps.infierno.hours]} />
                    </div>
                </article>

                <article className="border-2 border-[var(--warning)] bg-[#0d0d0d]">
                    <div className="bg-[var(--warning)] text-black px-5 py-2 font-mono text-[11px] uppercase tracking-[0.25em] flex justify-between items-center">
                        <span>
                            <Compass className="inline w-3.5 h-3.5 mr-2 -mt-0.5" />
                            Mi Mapa de Salida
                        </span>
                        <span>Ruta nueva</span>
                    </div>
                    <div className="p-6 space-y-3 font-mono text-sm">
                        <Row k="Persona segura" v={maps.salida.persona_segura} />
                        <Row k="Límite urgente" v={maps.salida.limite} />
                        <Row k="Propósito" v={maps.salida.proposito} />
                        <ListRow k="Acciones inmediatas" arr={maps.salida.acciones} />
                        <ListRow k="Frases ancla" arr={maps.salida.frases_ancla} italic />
                        <ListRow k="Hábitos base" arr={maps.salida.habitos} />
                        <ListRow k="Límites estructurales" arr={maps.salida.limites_estructurales} />
                    </div>
                </article>
            </div>

            {/* Editable comparison table */}
            <article className="dossier" data-testid="comparison-table">
                <div className="dossier-header">
                    <span>Tabla comparativa · Ruta vieja → Ruta nueva</span>
                    <span className="text-[var(--danger)]">Editable</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-[#1a1a1a]">
                            <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)]">
                                <th className="text-left p-3 w-24">Tipo</th>
                                <th className="text-left p-3">Ruta vieja</th>
                                <th className="text-left p-3">Acción contraria</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {comparison.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="p-8 text-center font-mono text-sm text-[var(--ink-muted)]">
                                        No hay filas aún. Vuelve a los pasos
                                        anteriores y marca al menos una señal,
                                        emoción, mentira, lugar o consecuencia.
                                    </td>
                                </tr>
                            )}
                            {comparison.map((row, i) => (
                                <tr
                                    key={i}
                                    className="border-t border-[var(--line)] hover:bg-[#1a1a1a]"
                                    data-testid={`comparison-row-${i}`}
                                >
                                    <td className="p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--danger)] align-top">
                                        {row.category}
                                    </td>
                                    <td className="p-3 font-mono text-sm text-[var(--paper-2)] align-top">
                                        {row.old}
                                    </td>
                                    <td className="p-3 align-top">
                                        <input
                                            type="text"
                                            value={row.new}
                                            onChange={(e) =>
                                                editRow(i, "new", e.target.value)
                                            }
                                            className="w-full bg-transparent border-b border-dashed border-[var(--warning)] font-mono text-sm text-[var(--warning)] focus:outline-none focus:border-solid focus:border-[var(--paper-2)] focus:text-[var(--paper-2)] py-1"
                                            data-testid={`comparison-edit-${i}`}
                                        />
                                    </td>
                                    <td className="p-3 align-top">
                                        <button
                                            onClick={() => removeRow(i)}
                                            className="p-1.5 text-[var(--ink-muted)] hover:text-[var(--danger)]"
                                            title="Eliminar"
                                            data-testid={`comparison-remove-${i}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] border-t border-[var(--line)] flex items-center gap-2">
                    <Edit3 className="w-3 h-3" />
                    Toca cualquier acción contraria para reescribirla con tus palabras.
                </p>
            </article>

            {/* 24h plan */}
            <article className="dossier">
                <div className="dossier-header">
                    <span>
                        <HeartPulse className="inline w-3.5 h-3.5 mr-2 -mt-0.5" />
                        Plan de 24 horas
                    </span>
                    <span className="text-[var(--danger)]">Si aparece craving</span>
                </div>
                <div className="p-6 grid sm:grid-cols-2 gap-2 font-mono text-sm text-[var(--paper)]">
                    {maps.plan24h.map((p, i) => (
                        <p
                            key={p}
                            className="border-l-2 border-[var(--danger)] pl-3 py-1"
                        >
                            <span className="text-[var(--ink-dim)] mr-2">
                                {String(i + 1).padStart(2, "0")}/
                            </span>
                            {p}
                        </p>
                    ))}
                </div>
            </article>

            {/* AI guide section */}
            <article className="dossier">
                <div className="dossier-header">
                    <span>
                        <Sparkles className="inline w-3.5 h-3.5 mr-2 -mt-0.5" />
                        Guía personalizada · IA
                    </span>
                    <span className="text-[var(--warning)]">Opcional</span>
                </div>
                <div className="p-6 md:p-8">
                    {!aiGuide && !aiLoading && (
                        <div>
                            <p className="font-mono text-sm text-[var(--paper)] leading-relaxed mb-4">
                                Si quieres, generamos una guía personal corta
                                basada en lo que escribiste. Voz nitanadicto.
                                Sin promesas. Solo lo aplicable.
                            </p>
                            <button
                                onClick={fetchAIGuide}
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="ai-generate"
                            >
                                <Sparkles className="w-4 h-4" />
                                Generar guía personal
                            </button>
                        </div>
                    )}
                    {aiLoading && (
                        <p className="font-mono text-sm text-[var(--ink-muted)] italic">
                            Leyendo tu mapa…
                        </p>
                    )}
                    {aiGuide && (
                        <div
                            className="prose-dark max-w-none"
                            data-testid="ai-guide-content"
                            dangerouslySetInnerHTML={{
                                __html: simpleMarkdown(aiGuide),
                            }}
                        />
                    )}
                </div>
            </article>

            {/* Actions */}
            <div className="flex flex-wrap gap-3" data-testid="result-actions">
                <button
                    onClick={downloadPdf}
                    className="btn-brutal btn-brutal-danger"
                    disabled={pdfBusy}
                    data-testid="result-download-pdf"
                >
                    <Download className="w-4 h-4" />
                    {pdfBusy ? "Generando…" : "Descargar mi mapa en PDF"}
                </button>
                <button
                    onClick={copyText}
                    className="btn-brutal"
                    data-testid="result-copy"
                >
                    <Copy className="w-4 h-4" />
                    Copiar mi plan
                </button>
                {identity.email && (
                    <button
                        onClick={emailMe}
                        className="btn-brutal btn-brutal-ghost"
                        disabled={emailLoading}
                        data-testid="result-email"
                    >
                        <Mail className="w-4 h-4" />
                        {emailLoading ? "Enviando…" : "Enviarme por correo"}
                    </button>
                )}
                <button
                    onClick={onReset}
                    className="btn-brutal btn-brutal-ghost"
                    data-testid="result-reset"
                >
                    <RefreshCw className="w-4 h-4" />
                    Reiniciar
                </button>
                <Link
                    to="/salida"
                    className="btn-brutal"
                    data-testid="result-emergency"
                >
                    <Shield className="w-4 h-4" />
                    Salida de emergencia
                </Link>
            </div>

            {/* Hidden printable template */}
            <div
                style={{ position: "absolute", left: "-9999px", top: 0 }}
                aria-hidden="true"
            >
                <PrintableMap
                    ref={pdfRef}
                    code={code}
                    today={today}
                    identity={identity}
                    maps={maps}
                    comparison={comparison}
                    aiGuide={aiGuide}
                />
            </div>
        </div>
    );
}

function Row({ k, v, danger }) {
    return (
        <div className="border-b border-dashed border-[var(--ink-dim)] pb-2 last:border-0 last:pb-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)]">
                {k}
            </p>
            <p
                className={`font-mono text-sm ${
                    danger ? "text-[var(--danger)] font-bold" : "text-[var(--paper-2)]"
                } leading-relaxed break-words`}
            >
                {v || "—"}
            </p>
        </div>
    );
}

function ListRow({ k, arr, italic }) {
    return (
        <div className="border-b border-dashed border-[var(--ink-dim)] pb-2 last:border-0 last:pb-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)] mb-1">
                {k}
            </p>
            {(arr || []).length === 0 ? (
                <p className="font-mono text-sm text-[var(--ink-muted)]">—</p>
            ) : (
                <p
                    className={`font-mono text-sm ${italic ? "italic text-[var(--warning)]" : "text-[var(--paper)]"} leading-relaxed`}
                >
                    {arr.join(" · ")}
                </p>
            )}
        </div>
    );
}

// Very small markdown renderer (only #, ##, ###, **bold**, lists, paragraphs).
function simpleMarkdown(text) {
    if (!text) return "";
    const escapeHTML = (s) =>
        s
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    const lines = text.split("\n");
    const out = [];
    let inList = false;
    function closeList() {
        if (inList) {
            out.push("</ul>");
            inList = false;
        }
    }
    for (const raw of lines) {
        const line = raw.trim();
        if (!line) {
            closeList();
            out.push("");
            continue;
        }
        if (line.startsWith("### ")) {
            closeList();
            out.push(`<h3>${inline(escapeHTML(line.slice(4)))}</h3>`);
            continue;
        }
        if (line.startsWith("## ")) {
            closeList();
            out.push(`<h2>${inline(escapeHTML(line.slice(3)))}</h2>`);
            continue;
        }
        if (line.startsWith("# ")) {
            closeList();
            out.push(`<h2>${inline(escapeHTML(line.slice(2)))}</h2>`);
            continue;
        }
        if (line.startsWith("- ") || line.startsWith("* ")) {
            if (!inList) {
                out.push("<ul>");
                inList = true;
            }
            out.push(`<li>${inline(escapeHTML(line.slice(2)))}</li>`);
            continue;
        }
        if (line === "---") {
            closeList();
            out.push("<hr/>");
            continue;
        }
        closeList();
        out.push(`<p>${inline(escapeHTML(line))}</p>`);
    }
    closeList();
    return out.join("\n");

    function inline(s) {
        return s
            .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
            .replace(/\*(.+?)\*/g, "<em>$1</em>");
    }
}

// ---------- Printable (PDF) template ----------
// Diseño tipo libro: portada, dossier, tabla, plan, guía IA, cierre con
// CTA SinAdicciones.org + "quién soy" + footer elmapainverso.com.
// Pensado para A4 portrait — cada sección "pdf-page" es una página A4 completa.
const PrintableMap = React.forwardRef(function PrintableMap(
    { code, today, identity, maps, comparison, aiGuide },
    ref,
) {
    // Paleta del libro
    const ink = "#e8e6e1";
    const mute = "#a8a098";
    const dim = "#6b6259";
    const paper = "#111111";
    const paper2 = "#0a0a0a";
    const red = "#D32F2F";
    const yel = "#FFB300";

    const styles = {
        // Cada página es A4 completa con margen interior consistente.
        page: {
            background: paper,
            color: ink,
            fontFamily: "IBM Plex Mono, ui-monospace, Menlo, monospace",
            padding: "22mm 20mm",
            width: "210mm",
            minHeight: "297mm",
            boxSizing: "border-box",
            pageBreakAfter: "always",
            position: "relative",
            overflow: "hidden",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
        },
        pageLast: {
            background: paper,
            color: ink,
            fontFamily: "IBM Plex Mono, ui-monospace, Menlo, monospace",
            padding: "22mm 20mm",
            width: "210mm",
            minHeight: "297mm",
            boxSizing: "border-box",
            position: "relative",
            overflow: "hidden",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
        },
        h1Cover: {
            fontFamily: "Playfair Display, Georgia, serif",
            color: ink,
            fontSize: 76,
            margin: 0,
            lineHeight: 0.92,
            letterSpacing: "-0.01em",
            fontWeight: 700,
        },
        h1Section: {
            fontFamily: "Playfair Display, Georgia, serif",
            color: ink,
            fontSize: 34,
            margin: "0 0 8px 0",
            lineHeight: 1,
            letterSpacing: "-0.005em",
        },
        meta: {
            fontSize: 9,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            color: yel,
            margin: 0,
        },
        sep: {
            border: 0,
            borderTop: "1px dashed " + dim,
            margin: "14px 0",
        },
        body: { fontSize: 11, lineHeight: 1.55, margin: 0 },
        bodySmall: { fontSize: 10, lineHeight: 1.55, margin: 0, color: mute },
        h2Red: {
            fontFamily: "Playfair Display, Georgia, serif",
            color: red,
            fontSize: 20,
            margin: "0 0 8px 0",
            lineHeight: 1.05,
        },
        h2Yel: {
            fontFamily: "Playfair Display, Georgia, serif",
            color: yel,
            fontSize: 20,
            margin: "0 0 8px 0",
            lineHeight: 1.05,
        },
        h2Ink: {
            fontFamily: "Playfair Display, Georgia, serif",
            color: ink,
            fontSize: 22,
            margin: "0 0 10px 0",
            lineHeight: 1.05,
        },
        label: {
            fontSize: 8,
            letterSpacing: "0.18em",
            color: mute,
            textTransform: "uppercase",
            margin: "0 0 3px 0",
            fontWeight: 600,
        },
        block: { marginBottom: 9 },
        column: {
            flex: "1 1 0%",
            minWidth: 0,
            padding: "12px 14px",
            boxSizing: "border-box",
        },
        twoCol: {
            display: "flex",
            gap: 12,
            marginTop: 6,
        },
        small: { fontSize: 9, color: mute, lineHeight: 1.5, margin: 0 },
        table: {
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 10,
            tableLayout: "fixed",
        },
        th: {
            background: "#1a1a1a",
            color: mute,
            padding: "6px 8px",
            fontSize: 8,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            textAlign: "left",
            borderBottom: "1px solid #2a2a2a",
        },
        td: {
            padding: "8px 8px",
            borderBottom: "1px dashed #2a2a2a",
            verticalAlign: "top",
            wordBreak: "break-word",
        },
        tag: {
            fontSize: 8,
            color: red,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontWeight: 700,
        },
        footerBar: {
            position: "absolute",
            left: "20mm",
            right: "20mm",
            bottom: "12mm",
            display: "flex",
            justifyContent: "space-between",
            fontSize: 8,
            color: dim,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
        },
    };

    const safe = (arr) =>
        arr && arr.length ? arr.join(" · ") : "—";

    const footer = (pageNum) => (
        <div style={styles.footerBar}>
            <span>EMI · {code}</span>
            <span style={{ color: yel }}>elmapainverso.com</span>
            <span>{pageNum}</span>
        </div>
    );

    return (
        <div ref={ref}>
            {/* ============================================================
                 PÁGINA 1 — PORTADA estilo libro
                ============================================================ */}
            <div style={styles.page}>
                <div>
                    <p style={styles.meta}>El Mapa Inverso · Expediente personal</p>
                    <div style={{ marginTop: 16 }}>
                        <hr style={{ ...styles.sep, borderTopColor: red, borderTopWidth: 2 }} />
                    </div>
                </div>

                <div style={{ marginTop: "32mm" }}>
                    <p
                        style={{
                            ...styles.label,
                            color: red,
                            fontSize: 10,
                            letterSpacing: "0.32em",
                            marginBottom: 10,
                        }}
                    >
                        Tu mapa.
                    </p>
                    <h1 style={styles.h1Cover}>
                        El Principio
                        <br />
                        del Mapa
                        <br />
                        <span style={{ color: red }}>Inverso.</span>
                    </h1>
                    <p
                        style={{
                            fontFamily: "Playfair Display, Georgia, serif",
                            fontStyle: "italic",
                            fontSize: 18,
                            color: mute,
                            marginTop: 20,
                            lineHeight: 1.4,
                        }}
                    >
                        La ruta que te llevó al fondo,
                        <br />
                        leída al revés.
                    </p>
                </div>

                <div
                    style={{
                        marginTop: "auto",
                        position: "absolute",
                        bottom: "30mm",
                        left: "20mm",
                        right: "20mm",
                    }}
                >
                    <hr style={{ ...styles.sep, marginTop: 0 }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                        <div>
                            <p style={styles.label}>Expediente</p>
                            <p
                                style={{
                                    fontFamily: "Playfair Display, Georgia, serif",
                                    fontSize: 22,
                                    color: ink,
                                    margin: 0,
                                    lineHeight: 1.1,
                                }}
                            >
                                {code}
                            </p>
                            {identity.name && (
                                <p style={{ ...styles.body, marginTop: 6 }}>
                                    A nombre de <strong style={{ color: yel }}>{identity.name}</strong>
                                    {identity.country ? ` · ${identity.country}` : ""}
                                </p>
                            )}
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <p style={styles.label}>Generado</p>
                            <p
                                style={{
                                    fontFamily: "Playfair Display, Georgia, serif",
                                    fontSize: 16,
                                    color: ink,
                                    margin: 0,
                                }}
                            >
                                {today}
                            </p>
                            <p style={{ ...styles.small, marginTop: 4 }}>Nelson González · @nitanadicto</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================
                 PÁGINA 2 — DOSSIER (2 columnas)
                ============================================================ */}
            <div style={styles.page}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                    <div>
                        <p style={styles.meta}>Capítulo 01 · Los dos mapas</p>
                        <h1 style={styles.h1Section}>Mapa Inverso</h1>
                    </div>
                    <p style={styles.small}>{today}</p>
                </div>
                <hr style={styles.sep} />

                <div style={styles.twoCol}>
                    {/* INFIERNO */}
                    <div style={{ ...styles.column, border: "1px solid " + red }}>
                        <h2 style={styles.h2Red}>Mi Mapa al Infierno</h2>
                        <p style={{ ...styles.label, marginBottom: 8 }}>Ruta vieja</p>

                        <div style={styles.block}>
                            <p style={styles.label}>Sustancia / conducta</p>
                            <p style={styles.body}>{maps.infierno.sustancia || "—"}</p>
                        </div>
                        <div style={styles.block}>
                            <p style={styles.label}>Estado · urgencia</p>
                            <p style={styles.body}>
                                {maps.infierno.estado || "—"} · {maps.infierno.urgencia || "—"}
                            </p>
                        </div>
                        <div style={styles.block}>
                            <p style={styles.label}>Señales</p>
                            <p style={styles.body}>{safe(maps.infierno.signals)}</p>
                        </div>
                        <div style={styles.block}>
                            <p style={styles.label}>Emociones</p>
                            <p style={styles.body}>{safe(maps.infierno.emotions)}</p>
                        </div>
                        <div style={styles.block}>
                            <p style={styles.label}>Mentiras</p>
                            <p style={{ ...styles.body, fontStyle: "italic" }}>{safe(maps.infierno.lies)}</p>
                        </div>
                        <div style={styles.block}>
                            <p style={styles.label}>Lugares · personas · horarios</p>
                            <p style={styles.body}>
                                {safe([
                                    ...maps.infierno.places,
                                    ...maps.infierno.people,
                                    ...maps.infierno.hours,
                                ])}
                            </p>
                        </div>
                    </div>

                    {/* SALIDA */}
                    <div style={{ ...styles.column, border: "1px solid " + yel }}>
                        <h2 style={styles.h2Yel}>Mi Mapa de Salida</h2>
                        <p style={{ ...styles.label, marginBottom: 8 }}>Ruta nueva</p>

                        <div style={styles.block}>
                            <p style={styles.label}>Persona segura</p>
                            <p style={styles.body}>{maps.salida.persona_segura || "—"}</p>
                        </div>
                        <div style={styles.block}>
                            <p style={styles.label}>Límite urgente</p>
                            <p style={styles.body}>{maps.salida.limite || "—"}</p>
                        </div>
                        <div style={styles.block}>
                            <p style={styles.label}>Propósito</p>
                            <p style={styles.body}>{maps.salida.proposito || "—"}</p>
                        </div>
                        <div style={styles.block}>
                            <p style={styles.label}>Acciones inmediatas</p>
                            <p style={styles.body}>{safe(maps.salida.acciones)}</p>
                        </div>
                        <div style={styles.block}>
                            <p style={styles.label}>Frases ancla</p>
                            <p style={{ ...styles.body, fontStyle: "italic" }}>{safe(maps.salida.frases_ancla)}</p>
                        </div>
                        <div style={styles.block}>
                            <p style={styles.label}>Hábitos base · límites</p>
                            <p style={styles.body}>
                                {safe([
                                    ...maps.salida.habitos,
                                    ...maps.salida.limites_estructurales,
                                ])}
                            </p>
                        </div>
                    </div>
                </div>
                {footer("02")}
            </div>

            {/* ============================================================
                 PÁGINA 3 — TABLA COMPARATIVA
                ============================================================ */}
            <div style={styles.page}>
                <p style={styles.meta}>Capítulo 02 · Ruta vieja → Ruta nueva</p>
                <h1 style={styles.h1Section}>
                    Tabla <span style={{ color: red }}>comparativa</span>
                </h1>
                <p style={{ ...styles.body, color: mute, marginTop: 6, maxWidth: "150mm" }}>
                    El mapa al infierno se lee al revés. Por cada señal que te llevaba al fondo, una acción contraria que apunta a la salida.
                </p>
                <hr style={styles.sep} />

                <table style={styles.table}>
                    <colgroup>
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "42%" }} />
                        <col style={{ width: "42%" }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th style={styles.th}>Tipo</th>
                            <th style={styles.th}>Ruta vieja</th>
                            <th style={{ ...styles.th, color: yel }}>Acción contraria</th>
                        </tr>
                    </thead>
                    <tbody>
                        {comparison.map((row, i) => (
                            <tr key={i}>
                                <td style={styles.td}>
                                    <span style={styles.tag}>{row.category}</span>
                                </td>
                                <td style={styles.td}>{row.old}</td>
                                <td style={{ ...styles.td, color: yel }}>{row.new}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {footer("03")}
            </div>

            {/* ============================================================
                 PÁGINA 4 — PLAN 24H + GUÍA IA
                ============================================================ */}
            <div style={styles.page}>
                <p style={styles.meta}>Capítulo 03 · Las próximas 24 horas</p>
                <h1 style={styles.h1Section}>
                    Plan de <span style={{ color: yel }}>24 horas</span>
                </h1>
                <hr style={styles.sep} />

                <ol style={{ ...styles.body, paddingLeft: 18, marginTop: 4 }}>
                    {maps.plan24h.map((p, i) => (
                        <li key={i} style={{ marginBottom: 5 }}>
                            {p}
                        </li>
                    ))}
                </ol>

                {aiGuide && (
                    <>
                        <hr style={styles.sep} />
                        <p style={styles.meta}>Anexo · Guía personalizada</p>
                        <h2 style={{ ...styles.h2Ink, marginTop: 6 }}>
                            Para ti, <span style={{ color: yel }}>específicamente.</span>
                        </h2>
                        <div
                            style={{ ...styles.body, color: ink, marginTop: 4 }}
                            dangerouslySetInnerHTML={{ __html: simpleMarkdown(aiGuide) }}
                        />
                    </>
                )}
                {footer("04")}
            </div>

            {/* ============================================================
                 PÁGINA 5 — CIERRE: SinAdicciones + Quién soy + Footer
                ============================================================ */}
            <div style={styles.pageLast}>
                <p style={styles.meta}>Capítulo final · Donde sigue el camino</p>
                <h1 style={styles.h1Section}>
                    Este mapa no <span style={{ color: red }}>te salva.</span>
                </h1>
                <p style={{ ...styles.body, color: mute, marginTop: 6, maxWidth: "150mm" }}>
                    Te muestra. La salvación no la firma un papel: la firman tus próximas 24 horas,
                    tu próxima persona segura, tu próximo &ldquo;no&rdquo; a tiempo.
                </p>
                <hr style={styles.sep} />

                {/* CTA SinAdicciones */}
                <div
                    style={{
                        border: "1px solid " + yel,
                        padding: "16px 18px",
                        marginTop: 4,
                        marginBottom: 18,
                    }}
                >
                    <p style={{ ...styles.label, color: yel, marginBottom: 6 }}>
                        Si necesitas ayuda hoy
                    </p>
                    <h2 style={{ ...styles.h2Yel, fontSize: 24, marginBottom: 8 }}>
                        Conoce SinAdicciones.org
                    </h2>
                    <p style={{ ...styles.body, marginBottom: 6 }}>
                        Plataforma que conecta a <strong>familias con tratamiento verificado</strong> e información
                        sin letra chica en Latinoamérica. Ofrece <strong>orientación gratuita</strong>, guías paso a paso
                        y herramientas para comparar sin letra chica y dar el primer paso.
                    </p>
                    <p style={{ ...styles.bodySmall, marginTop: 8 }}>
                        ▸ Tratamientos · guía · orientación familiar · derivación a especialistas
                    </p>
                    <p style={{ ...styles.body, marginTop: 10, color: yel, fontWeight: 700 }}>
                        sinadicciones.org
                    </p>
                </div>

                {/* Quién soy */}
                <p style={styles.meta}>Quién firma este mapa</p>
                <h2 style={{ ...styles.h2Ink, marginTop: 6 }}>
                    Soy <span style={{ color: red }}>Nelson González Rodríguez</span>.
                </h2>
                <p style={{ ...styles.body, marginTop: 4 }}>
                    Más de veinte años en adicción activa. Tres centros de rehabilitación.
                    Comisarías, recaídas, promesas rotas y una vida paralela que destruí de a poco.
                    Hoy sostengo mi sobriedad un día a la vez.
                </p>
                <p style={{ ...styles.body, marginTop: 8 }}>
                    Escribí <em>El Principio del Mapa Inverso</em> y fundé <strong>SinAdicciones.org</strong> porque
                    la información que me habría ahorrado años de desastre no existía gratis, en mi idioma, sin moralina.
                    Esta es esa información.
                </p>
                <p style={{ ...styles.body, marginTop: 10, color: mute, fontStyle: "italic" }}>
                    Mi misión: que nadie tenga que armar este mapa solo.
                </p>

                <hr style={{ ...styles.sep, marginTop: 22 }} />

                <p style={styles.small}>
                    Este mapa no reemplaza terapia, evaluación médica ni ayuda profesional.
                    Si estás en riesgo, busca ayuda inmediata.
                </p>

                <div style={styles.footerBar}>
                    <span>EMI · {code}</span>
                    <span style={{ color: yel }}>elmapainverso.com</span>
                    <span>@nitanadicto · sinadicciones.org</span>
                </div>
            </div>
        </div>
    );
});

// ---------- Validation helper ----------
function validateStep(stepIdx, answers) {
    switch (stepIdx) {
        case 1: // starting
            return Boolean(
                (answers.starting?.sustancia || "").trim() &&
                    answers.starting?.estado &&
                    answers.starting?.urgencia,
            );
        case 2: // signals
        case 3: // emotions
        case 4: // lies
            return chipValues(answers[stepFieldMap[stepIdx]]).length > 0;
        case 5: {
            // triggers — at least one across the 3
            const t = answers.triggers || {};
            return (
                chipValues(t.places).length +
                    chipValues(t.people).length +
                    chipValues(t.hours).length >
                0
            );
        }
        case 6: {
            const c = answers.consequences || {};
            return (
                chipValues(c.body).length +
                    chipValues(c.bonds).length +
                    chipValues(c.money).length +
                    chipValues(c.dignity).length >
                0
            );
        }
        case 7: {
            const e = answers.immediate_exit || {};
            return (
                (e.persona_segura || "").trim() &&
                (chipValues(e.actions).length > 0 ||
                    chipValues(e.anchor).length > 0)
            );
        }
        case 8: {
            const r = answers.recovery_system || {};
            return (
                chipValues(r.habits).length + chipValues(r.limits).length > 0
            );
        }
        default:
            return true;
    }
}

const stepFieldMap = { 2: "signals", 3: "emotions", 4: "lies" };

// ---------- MAIN ----------
export default function MapaInverso() {
    const draft = useMemo(() => loadDraft(), []);
    const [stepIdx, setStepIdx] = useState(0);
    const [answers, setAnswers] = useState(EMPTY_ANSWERS);
    const [identity, setIdentity] = useState({
        name: "",
        email: "",
        country: "",
    });
    const [crisisOpen, setCrisisOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [aiGuide, setAiGuide] = useState("");
    const pdfRef = useRef(null);
    const hasDraft = !!draft;

    // Persist drafts during the questionnaire (not on welcome/result)
    useEffect(() => {
        if (stepIdx > 0 && stepIdx < STEPS.length - 1) {
            persistDraft({ stepIdx, answers, identity });
        }
    }, [stepIdx, answers, identity]);

    function resume() {
        if (!draft) return;
        setAnswers(draft.answers || EMPTY_ANSWERS);
        setIdentity(draft.identity || identity);
        const target = draft.stepIdx || 1;
        setStepIdx(Math.min(target, STEPS.length - 2));
        toast.message("Sesión retomada", {
            description: "Continuamos donde lo dejaste.",
        });
    }

    function start() {
        setStepIdx(1);
        track("wizard_started", { resumed: false });
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function reset() {
        clearDraft();
        setAnswers(EMPTY_ANSWERS);
        setIdentity({ name: "", email: "", country: "" });
        setSessionId(null);
        setAiGuide("");
        setStepIdx(0);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    const goNext = useCallback(async () => {
        // Crisis check on global answers
        if (scanCrisis(answers) && !crisisOpen) {
            setCrisisOpen(true);
            return;
        }
        if (!validateStep(stepIdx, answers)) {
            toast.error("Selecciona al menos una opción para continuar.");
            return;
        }
        // Last step before result -> submit (optional save)
        const lastQuestionStep = STEPS.length - 2; // 8
        if (stepIdx === lastQuestionStep) {
            setSubmitting(true);
            try {
                const maps = buildMaps(answers);
                const res = await api.post("/mapa-inverso/submit", {
                    name: identity.name?.trim() || null,
                    email: identity.email?.trim() || null,
                    country: identity.country?.trim() || null,
                    consent_to_research: true,
                    crisis_flagged: scanCrisis(answers),
                    answers,
                    summary: maps,
                });
                setSessionId(res.data?.id || null);
                if (res.data?.crisis_flagged) setCrisisOpen(true);
                clearDraft();
                setStepIdx(STEPS.length - 1);
                track("wizard_completed", {
                    saved: !!res.data?.saved,
                    crisis: !!res.data?.crisis_flagged,
                    country: identity.country || "",
                });
                window.scrollTo({ top: 0, behavior: "smooth" });
            } catch (e) {
                toast.error(
                    formatApiError(e.response?.data?.detail) ||
                        "No se pudo continuar. Intenta de nuevo.",
                );
            } finally {
                setSubmitting(false);
            }
            return;
        }
        setStepIdx((s) => Math.min(s + 1, STEPS.length - 1));
        track("wizard_step", { from: stepIdx, to: stepIdx + 1 });
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [answers, crisisOpen, identity, stepIdx]);

    function goBack() {
        setStepIdx((s) => Math.max(s - 1, 0));
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    const step = STEPS[stepIdx];
    const totalSteps = STEPS.length - 1; // exclude welcome
    const progressPct =
        stepIdx === 0 ? 0 : Math.round((stepIdx / totalSteps) * 100);

    return (
        <>
            <SEO
                title="Mapa Inverso · expediente interactivo"
                description="Mapea la ruta de tu adicción en 9 pasos guiados con chips y construye su ruta contraria. Una herramienta de autoconciencia de Autodestrucción S.A."
                path="/mapa-inverso"
                keywords="adicción, recuperación, mapa, autoconciencia, plan de acción, NiTanAdicto"
            />

            {crisisOpen && <CrisisBanner onClose={() => setCrisisOpen(false)} />}

            <section className="py-10 md:py-16" data-testid="mapa-inverso-section">
                <div className="container-x">
                    {/* Top bar */}
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-8 pb-6 border-b border-[var(--line)]">
                        <div>
                            <p className="eyebrow mb-2">Departamento Mapa Inverso</p>
                            <h1 className="font-display text-3xl md:text-5xl text-[var(--paper-2)] leading-[0.95]">
                                Paso {step.code} · {step.title}
                            </h1>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mt-2">
                                {step.kicker} · Sesión efímera local · Privado hasta el envío
                            </p>
                        </div>
                        <Link
                            to="/salida"
                            className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--danger)] border-b border-[var(--danger)] inline-flex items-center gap-1 pb-0.5 hover:text-[var(--paper-2)] hover:border-[var(--paper-2)]"
                            data-testid="mapa-top-salida"
                        >
                            <AlertTriangle className="w-3 h-3" />
                            Salida
                        </Link>
                    </div>

                    {/* Progress */}
                    {stepIdx > 0 && (
                        <div className="mb-10" data-testid="progress-bar-container">
                            <div className="flex justify-between items-center mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)]">
                                <span>
                                    Avance · paso {stepIdx} de {totalSteps}
                                </span>
                                <span className="text-[var(--warning)]">
                                    {progressPct}%
                                </span>
                            </div>
                            <div className="h-2 w-full bg-[var(--line)] relative overflow-hidden">
                                <div
                                    className="h-full bg-[var(--danger)] transition-all duration-500"
                                    style={{ width: `${progressPct}%` }}
                                    data-testid="progress-bar-fill"
                                />
                                <div className="hazard-stripes-thin absolute inset-0 opacity-20 pointer-events-none" />
                            </div>
                            <div className="grid grid-cols-9 gap-1 mt-3">
                                {STEPS.slice(1).map((s, i) => (
                                    <button
                                        key={s.id}
                                        onClick={() =>
                                            i + 1 <= stepIdx && setStepIdx(i + 1)
                                        }
                                        disabled={i + 1 > stepIdx}
                                        className={`h-7 text-[9px] font-mono uppercase tracking-[0.15em] border ${
                                            i + 1 === stepIdx
                                                ? "bg-[var(--danger)] text-[var(--paper-2)] border-[var(--danger)]"
                                                : i + 1 < stepIdx
                                                  ? "border-[var(--warning)] text-[var(--warning)]"
                                                  : "border-[var(--line)] text-[var(--ink-dim)]"
                                        } disabled:cursor-not-allowed`}
                                        data-testid={`progress-pip-${i + 1}`}
                                        title={s.title}
                                    >
                                        {i + 1 < stepIdx ? (
                                            <Check className="w-3 h-3 mx-auto" />
                                        ) : (
                                            s.code
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Steps */}
                    {stepIdx === 0 && (
                        <StepWelcome
                            onStart={start}
                            hasDraft={hasDraft}
                            onResume={resume}
                        />
                    )}
                    {stepIdx === 1 && (
                        <StepStarting data={answers} setData={setAnswers} />
                    )}
                    {stepIdx === 2 && (
                        <ChipStep
                            data={answers}
                            setData={setAnswers}
                            code="02"
                            title="Primera señal"
                            subtitle="¿Cómo empieza tu ruta al infierno?"
                            field="signals"
                            question="Marca las señales que aparecen primero cuando tu ruta vieja se enciende. Sé generoso: cuantas más marques, mejor el mapa."
                            options={SIGNAL_OPTIONS}
                            annotation="Una recaída rara vez empieza con la droga. Casi siempre empieza con una mentira pequeña."
                        />
                    )}
                    {stepIdx === 3 && (
                        <ChipStep
                            data={answers}
                            setData={setAnswers}
                            code="03"
                            title="Emoción dominante"
                            subtitle="¿Qué emoción suele empujar tu ruta?"
                            field="emotions"
                            question="Elige las emociones que te llevan a buscar consumo o evasión."
                            options={EMOTION_OPTIONS}
                            annotation="No tienes que escribir perfecto. Tienes que decir la verdad."
                        />
                    )}
                    {stepIdx === 4 && (
                        <ChipStep
                            data={answers}
                            setData={setAnswers}
                            code="04"
                            title="Mentira principal"
                            subtitle="Las frases del autoengaño"
                            field="lies"
                            question="¿Qué frase usa tu versión adicta para acercarte al consumo?"
                            options={LIE_OPTIONS}
                            annotation="Si nombras la mentira, dejas de creértela en silencio."
                        />
                    )}
                    {stepIdx === 5 && (
                        <StepTriggers data={answers} setData={setAnswers} />
                    )}
                    {stepIdx === 6 && (
                        <StepConsequences data={answers} setData={setAnswers} />
                    )}
                    {stepIdx === 7 && (
                        <StepImmediateExit data={answers} setData={setAnswers} />
                    )}
                    {stepIdx === 8 && (
                        <StepRecovery data={answers} setData={setAnswers} />
                    )}
                    {stepIdx === STEPS.length - 1 && (
                        <ResultView
                            identity={identity}
                            setIdentity={setIdentity}
                            answers={answers}
                            sessionId={sessionId}
                            onReset={reset}
                            pdfRef={pdfRef}
                            aiGuide={aiGuide}
                            setAiGuide={setAiGuide}
                        />
                    )}

                    {/* Navigation */}
                    {stepIdx > 0 && stepIdx < STEPS.length - 1 && (
                        <div className="flex flex-wrap justify-between gap-3 mt-10 pt-6 border-t border-dashed border-[var(--ink-dim)]">
                            <button
                                onClick={goBack}
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="step-back"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Atrás
                            </button>
                            <button
                                onClick={goNext}
                                disabled={submitting}
                                className="btn-brutal btn-brutal-danger disabled:opacity-60"
                                data-testid="step-next"
                            >
                                {stepIdx === STEPS.length - 2 ? (
                                    <>
                                        {submitting ? "Procesando…" : "Ver mi mapa"}
                                        <FileText className="w-4 h-4" />
                                    </>
                                ) : (
                                    <>
                                        Siguiente
                                        <ArrowRight className="w-4 h-4" />
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Disclaimer footer */}
                    <div className="mt-12 pt-6 border-t border-[var(--line)] text-center">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)]">
                            <Anchor className="inline w-3 h-3 mr-2 -mt-0.5" />
                            Este mapa no reemplaza terapia, psiquiatría ni ayuda
                            profesional.{" "}
                            <Link
                                to="/salida"
                                className="text-[var(--warning)] underline"
                            >
                                Recursos reales
                            </Link>
                            .
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
